import { useTranslation } from 'react-i18next';
import type { PaymentItem } from '@/mocks/dashboard';

interface Props {
  payments: PaymentItem[];
}

const statusColors: Record<string, string> = {
  pending: 'bg-warm-50 text-warm-700 border-warm-200',
  completed: 'bg-ink-50 text-ink-600 border-ink-200',
  failed: 'bg-warm-50 text-warm-800 border-warm-300',
  refunded: 'bg-ink-50 text-ink-400 border-ink-200',
};

export default function DashboardPayments({ payments }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-bank-card-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.payments.title')}</h3>
        </div>
        <a href="/my/payments" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {payments.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-bank-card-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.payments.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {payments.map((p) => (
            <div key={p.id} className="px-4 md:px-5 py-3 md:py-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-ink-300">{new Date(p.paymentDate).toLocaleDateString()}</span>
                  <span className="text-xs text-ink-200">•</span>
                  <span className="text-xs font-mono text-warm-400">{p.referenceNumber}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {p.methodBrand && p.methodLast4 && (
                    <span className="text-xs text-ink-300">
                      {p.methodBrand} •••• {p.methodLast4}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-sm font-semibold text-ink-600">
                  {p.currency} {p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusColors[p.status]}`}>
                  {t(`dashboard.paymentStatus.${p.status}`)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}